import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { KeyRound, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const inputClass =
    "w-full bg-[#151311] border border-white/10 focus:border-[#cfa052]/60 focus:outline-none text-[#e8e4db] placeholder:text-[#6e6a62] text-sm font-poetic rounded-sm px-4 py-3 pr-12 transition-colors";

function PasswordField({ label, value, onChange, testid, autoComplete, hint }) {
    const [visible, setVisible] = useState(false);
    return (
        <div>
            <label className="block font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a] mb-2">
                {label}
            </label>
            <div className="relative">
                <input
                    type={visible ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    data-testid={testid}
                    className={inputClass}
                />
                <button
                    type="button"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? "Hide password" : "Show password"}
                    data-testid={`${testid}-toggle`}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6e6a62] hover:text-[#cfa052] transition-colors"
                >
                    {visible ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
            </div>
            {hint && <p className="mt-2 font-poetic text-xs text-[#6e6a62]">{hint}</p>}
        </div>
    );
}

export default function AdminSecurity() {
    const { admin } = useAdminAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [lastChanged, setLastChanged] = useState(null);

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    const tooShort = next.length > 0 && next.length < 12;
    const mismatch = confirm.length > 0 && next !== confirm;
    const canSubmit =
        current.length > 0 && next.length >= 12 && next === confirm && !saving;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                currentPassword: current,
                newPassword: next,
            });
            toast.success("Password changed.", {
                description:
                    "Use the new password next time you sign in. Keep it somewhere safe.",
            });
            setLastChanged(new Date());
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <PortalLayout>
            <section
                className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32"
                data-testid="admin-security-page"
            >
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · security</span>
                    <h1
                        className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]"
                        data-testid="admin-security-title"
                    >
                        Change <span className="italic">password</span>
                    </h1>
                    <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                        Rotate your sign-in password anytime — no redeploy needed. Once you change
                        it here, the portal keeps your new password even across restarts.
                    </p>
                </div>

                <AdminSubNav />

                <form
                    onSubmit={handleSubmit}
                    className="max-w-md space-y-6"
                    data-testid="security-change-form"
                >
                    <PasswordField
                        label="Current password"
                        value={current}
                        onChange={setCurrent}
                        testid="security-current-input"
                        autoComplete="current-password"
                    />
                    <PasswordField
                        label="New password"
                        value={next}
                        onChange={setNext}
                        testid="security-new-input"
                        autoComplete="new-password"
                        hint="At least 12 characters. Mix letters, numbers and symbols — avoid words you've used elsewhere."
                    />
                    {tooShort && (
                        <p
                            className="font-poetic text-xs text-[#c96f4a] -mt-3"
                            data-testid="security-too-short"
                        >
                            New password needs at least 12 characters ({next.length}/12).
                        </p>
                    )}
                    <PasswordField
                        label="Confirm new password"
                        value={confirm}
                        onChange={setConfirm}
                        testid="security-confirm-input"
                        autoComplete="new-password"
                    />
                    {mismatch && (
                        <p
                            className="font-poetic text-xs text-[#c96f4a] -mt-3"
                            data-testid="security-mismatch"
                        >
                            Passwords don&apos;t match yet.
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={!canSubmit}
                        data-testid="security-submit-btn"
                        className="inline-flex items-center gap-2 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <KeyRound className="w-4 h-4" />
                        )}
                        Change password
                    </button>

                    {lastChanged && (
                        <p
                            className="flex items-center gap-2 font-poetic text-sm text-[#9a958a]"
                            data-testid="security-changed-note"
                        >
                            <ShieldCheck className="w-4 h-4 text-[#cfa052]" />
                            Password updated {lastChanged.toLocaleTimeString()} — it takes effect
                            immediately.
                        </p>
                    )}
                </form>
            </section>
        </PortalLayout>
    );
}
